// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import GridContainer from "@store/MobiusOverrides/GridContainer/GridContainer";
import { useStepperContextStep } from "@store/Framework/Components/StepperContext";
import { useShipToStateByCustomerSequence } from "@store/Framework/Store/Data/ShipTos/ShipTosHooks";
import cancelDraftFieldValues from "@store/Framework/Store/Greif/Components/PackagingAdviceForm/Handlers/CancelDraftFieldValues";
import saveDraftFieldValues from "@store/Framework/Store/Greif/Components/PackagingAdviceForm/Handlers/SaveDraftFieldValues";
import setDraftFieldValues from "@store/Framework/Store/Greif/Components/PackagingAdviceForm/Handlers/SetDraftFieldValues";
import { PackagingAdviceFormFieldValues } from "@store/Framework/Store/Greif/Components/PackagingAdviceForm/PackagingAdviceFormState";
import EmailAddressFormField, {
    emailAddressFormFieldLabelKey,
    EmailAddressFormFieldPresentationProps,
} from "@store/Library/Components/FormField/EmailAddressFormField";
import FirstNameFormField, {
    firstNameFormFieldLabelKey,
    FirstNameFormFieldPresentationProps,
} from "@store/Library/Components/FormField/FirstNameFormField";
import LastNameFormField, {
    lastNameFormFieldLabelKey,
    LastNameFormFieldPresentationProps,
} from "@store/Library/Components/FormField/LastNameFormField";
import PhoneNumberFormField, {
    phoneNumberFormFieldLabelKey,
    PhoneNumberFormFieldPresentationProps,
} from "@store/Library/Components/FormField/PhoneNumberFormField";
import ShipToFormField, {
    shipToFormFieldLabelKey,
    ShipToFormFieldPresentationProps,
} from "@store/Library/Components/FormField/ShipToFormField";
import FormStep, { FormStepPresentationProps, FormStepProps } from "@store/Library/Components/FormStepper/FormStep";
import { formStepProps } from "@store/Themes/common/props/formStep";
import React, { FC, useState } from "react";

const stepFieldValueKeys: Array<keyof PackagingAdviceFormFieldValues> = [
    "emailAddress",
    "firstName",
    "lastName",
    "phoneNumber",
    "shipToNumber",
];

export interface PackagingAdviceFormContactInformationStepProps
    extends Partial<Omit<FormStepProps, "contextStepNumber">> {}

export interface PackagingAdviceFormContactInformationStepStyles {
    emailAddressFormField?: EmailAddressFormFieldPresentationProps;
    firstNameFormField?: FirstNameFormFieldPresentationProps;
    formStep?: FormStepPresentationProps;
    formGridContainer?: GridContainerProps;
    formGridItem?: GridItemProps;
    infoGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    infoLabel?: TypographyPresentationProps;
    infoValue?: TypographyPresentationProps;
    lastNameFormField?: LastNameFormFieldPresentationProps;
    phoneNumberFormField?: PhoneNumberFormFieldPresentationProps;
    shipToFormField?: ShipToFormFieldPresentationProps;
}

export const packagingAdviceFormContactInformationStepStyles: PackagingAdviceFormContactInformationStepStyles = {
    formGridContainer: formStepProps.formGridContainer,
    formGridItem: formStepProps.formGridItem,
    infoGridContainer: formStepProps.infoGridContainer,
    infoGridItem: formStepProps.infoGridItem,
    infoLabel: formStepProps.infoLabel,
    infoValue: formStepProps.infoValue,
};

const styles = packagingAdviceFormContactInformationStepStyles;

const PackagingAdviceFormContactInformationStep: FC<PackagingAdviceFormContactInformationStepProps> = ({
    ...formStepProps
}) => {
    const dispatch = useAppDispatch();
    const [showErrors, setShowErrors] = useState(false);
    const { isComplete, stepNumber } = useStepperContextStep();
    const currentBillTo = useAppSelector(getCurrentBillToState)?.value;
    const draftFieldValues = useAppSelector(state => state.greif.components.packagingAdviceForm.formDraftFieldValues);
    const fieldValues = useAppSelector(state => state.greif.components.packagingAdviceForm.formFieldValues);
    const selectedShipTo = useShipToStateByCustomerSequence(fieldValues.shipToNumber.value);

    if (!stepNumber) {
        return null;
    }

    const emailAddressLabel = translate(`${emailAddressFormFieldLabelKey}`);
    const firstNameLabel = translate(`${firstNameFormFieldLabelKey}`);
    const lastNameLabel = translate(`${lastNameFormFieldLabelKey}`);
    const phoneNumberLabel = translate(`${phoneNumberFormFieldLabelKey}`);
    const shipToLabel = translate(`${shipToFormFieldLabelKey}`);
    const soldToLabel = translate("Sold To");

    return (
        <FormStep
            {...styles.formStep}
            contextStepNumber={stepNumber}
            onCancel={() => dispatch(cancelDraftFieldValues(stepFieldValueKeys))}
            onSubmit={event => {
                if (!showErrors) {
                    setShowErrors(true);
                }

                if (stepFieldValueKeys.some(x => !draftFieldValues[x].isValid)) {
                    event.preventDefault();
                    return;
                }

                dispatch(saveDraftFieldValues(stepFieldValueKeys));
            }}
            title={translate("Contact Information")}
            {...formStepProps}
        >
            {isComplete ? (
                <GridContainer {...styles.infoGridContainer}>
                    <GridItem {...styles.infoGridItem}>
                        <Typography {...styles.infoLabel}>{firstNameLabel}</Typography>
                        <Typography {...styles.infoValue}>{fieldValues.firstName.value || "—"}</Typography>
                    </GridItem>
                    <GridItem {...styles.infoGridItem}>
                        <Typography {...styles.infoLabel}>{lastNameLabel}</Typography>
                        <Typography {...styles.infoValue}>{fieldValues.lastName.value || "—"}</Typography>
                    </GridItem>
                    <GridItem {...styles.infoGridItem}>
                        <Typography {...styles.infoLabel}>{phoneNumberLabel}</Typography>
                        <Typography {...styles.infoValue}>{fieldValues.phoneNumber.value || "—"}</Typography>
                    </GridItem>
                    <GridItem {...styles.infoGridItem}>
                        <Typography {...styles.infoLabel}>{emailAddressLabel}</Typography>
                        <Typography {...styles.infoValue}>{fieldValues.emailAddress.value || "—"}</Typography>
                    </GridItem>
                    <GridItem {...styles.infoGridItem}>
                        <Typography {...styles.infoLabel}>{soldToLabel}</Typography>
                        <Typography {...styles.infoValue}>{currentBillTo?.label || "—"}</Typography>
                    </GridItem>
                    <GridItem {...styles.infoGridItem}>
                        <Typography {...styles.infoLabel}>{shipToLabel}</Typography>
                        <Typography {...styles.infoValue}>{selectedShipTo?.label || "—"}</Typography>
                    </GridItem>
                </GridContainer>
            ) : (
                <GridContainer {...styles.formGridContainer}>
                    <GridItem {...styles.formGridItem}>
                        <FirstNameFormField
                            {...styles.firstNameFormField}
                            defaultValue={draftFieldValues.firstName.value}
                            label={firstNameLabel}
                            onUpdate={firstName => dispatch(setDraftFieldValues({ firstName }))}
                            prefill
                            required
                            showError={showErrors}
                        />
                    </GridItem>
                    <GridItem {...styles.formGridItem}>
                        <LastNameFormField
                            {...styles.lastNameFormField}
                            defaultValue={draftFieldValues.lastName.value}
                            label={lastNameLabel}
                            onUpdate={lastName => dispatch(setDraftFieldValues({ lastName }))}
                            prefill
                            required
                            showError={showErrors}
                        />
                    </GridItem>
                    <GridItem {...styles.formGridItem}>
                        <PhoneNumberFormField
                            {...styles.phoneNumberFormField}
                            defaultValue={draftFieldValues.phoneNumber.value}
                            label={phoneNumberLabel}
                            onUpdate={phoneNumber => dispatch(setDraftFieldValues({ phoneNumber }))}
                            showError={showErrors}
                        />
                    </GridItem>
                    <GridItem {...styles.formGridItem}>
                        <EmailAddressFormField
                            {...styles.emailAddressFormField}
                            defaultValue={draftFieldValues.emailAddress.value}
                            label={emailAddressLabel}
                            onUpdate={emailAddress => dispatch(setDraftFieldValues({ emailAddress }))}
                            prefill
                            required
                            showError={showErrors}
                        />
                    </GridItem>
                    <GridItem {...styles.formGridItem}>
                        <TextField disabled label={soldToLabel} name="soldTo" value={currentBillTo?.label || ""} />
                    </GridItem>
                    <GridItem {...styles.formGridItem}>
                        <ShipToFormField
                            {...styles.shipToFormField}
                            defaultValue={draftFieldValues.shipToNumber.value}
                            onUpdate={shipToNumber => dispatch(setDraftFieldValues({ shipToNumber }))}
                            prefill
                            required
                            showError={showErrors}
                        />
                    </GridItem>
                </GridContainer>
            )}
        </FormStep>
    );
};

export default PackagingAdviceFormContactInformationStep;
